import styles from './catalog-picker-modal.module.css'

import { RxStarFilled } from '@react-icons/all-files/rx/RxStarFilled'
import React, { useEffect, useMemo, useState } from 'react'

import type { ClientLibraryId } from 'types/client-library'

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  SearchTextField,
  Tabs,
  Text,
} from 'components/ds'

import { CatalogImageOrFallback } from '../catalog-info-popover'
import { CatalogList } from './catalog-list'
import { SelectionBar } from './selection-bar'
import type { CatalogGroup, CatalogSelection, PickerCatalog } from './types'
import { catalogName, filterCatalogs, toggleId } from './utils'

export type { CatalogGroup, CatalogSelection, PickerCatalog } from './types'

type CatalogPickerModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalogs: PickerCatalog[]
  groups: CatalogGroup[]
  selection: CatalogSelection
  onApply: (selection: CatalogSelection) => void
}

/**
 * Lets the user narrow a search to specific catalogs they can reach across
 * DISCO, individually or via the DISCO-curated groups.
 */
export const CatalogPickerModal = ({
  open,
  onOpenChange,
  catalogs,
  groups,
  selection,
  onApply,
}: CatalogPickerModalProps) => {
  const [tab, setTab] = useState<'catalogs' | 'groups'>('catalogs')
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<CatalogSelection>(selection)

  // The parent controls `open`, so Radix never reports the opening; start each
  // session from the applied selection rather than a cancelled draft.
  useEffect(() => {
    if (open) {
      setDraft(selection)
      setQuery('')
    }
  }, [open, selection])

  const changeTab = (next: string) => {
    setTab(next as typeof tab)
    setQuery('')
  }

  const visibleCatalogs = useMemo(
    () => filterCatalogs(catalogs, query),
    [catalogs, query],
  )

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? groups.filter(g => g.name.toLowerCase().includes(q)) : groups
  }, [groups, query])

  const toggleCatalog = (id: ClientLibraryId) =>
    setDraft(d => ({ ...d, catalogIds: toggleId(d.catalogIds, id) }))

  const toggleGroup = (id: string) =>
    setDraft(d => ({ ...d, groupIds: toggleId(d.groupIds, id) }))

  const selectedCount = draft.catalogIds.length + draft.groupIds.length
  const catalogsById = useMemo(
    () => new Map(catalogs.map(c => [c.id, c])),
    [catalogs],
  )
  const groupCatalogs = (group: CatalogGroup) =>
    group.catalogIds
      .map(id => catalogsById.get(id))
      .filter((c): c is PickerCatalog => !!c)

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="520px" className={styles.content}>
        <Dialog.Title mb="1">Search specific catalogs</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Choose catalogs you have access to across DISCO, or pick a group to
          search several at once.
        </Dialog.Description>

        <Tabs.Root value={tab} onValueChange={changeTab}>
          <Tabs.List size="2">
            <Tabs.Trigger value="catalogs">
              <span>Catalogs</span>
              {draft.catalogIds.length > 0 && (
                <span>&nbsp;({draft.catalogIds.length})</span>
              )}
            </Tabs.Trigger>
            <Tabs.Trigger value="groups">
              <span>Groups</span>
              {draft.groupIds.length > 0 && (
                <span>&nbsp;({draft.groupIds.length})</span>
              )}
            </Tabs.Trigger>
          </Tabs.List>

          <Box mt="3">
            <SearchTextField
              placeholder={
                tab === 'catalogs' ? 'Search catalogs' : 'Search groups'
              }
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClear={() => setQuery('')}
            />
          </Box>

          <Tabs.Content value="catalogs">
            {draft.catalogIds.length > 0 && (
              <SelectionBar
                count={draft.catalogIds.length}
                onClear={() => setDraft(d => ({ ...d, catalogIds: [] }))}
              />
            )}
            <Box className={styles.list} mt="2">
              <CatalogList
                catalogs={visibleCatalogs}
                selectedIds={draft.catalogIds}
                onToggle={toggleCatalog}
              />
            </Box>
          </Tabs.Content>

          <Tabs.Content value="groups">
            {draft.groupIds.length > 0 ? (
              <SelectionBar
                count={draft.groupIds.length}
                onClear={() => setDraft(d => ({ ...d, groupIds: [] }))}
              />
            ) : (
              <Flex align="center" className={styles.listBar}>
                <Text size="2" color="gray">
                  {`${visibleGroups.length} ${
                    visibleGroups.length === 1 ? 'group' : 'groups'
                  }`}
                </Text>
              </Flex>
            )}
            <Box className={styles.list} mt="2">
              {visibleGroups.length === 0 ? (
                <Text size="2" color="gray" as="p" className={styles.empty}>
                  No groups match your search.
                </Text>
              ) : (
                visibleGroups.map(group => {
                  const members = groupCatalogs(group)
                  return (
                    <Flex
                      key={group.id}
                      align="center"
                      justify="between"
                      className={styles.row}
                    >
                      <Text as="label" size="2" className={styles.rowLabel}>
                        <Flex align="center" gap="3">
                          <Checkbox
                            checked={draft.groupIds.includes(group.id)}
                            onCheckedChange={() => toggleGroup(group.id)}
                          />
                          <div className={styles.avatarStack}>
                            {members.slice(0, 4).map(catalog => (
                              <CatalogImageOrFallback
                                key={catalog.id}
                                name={catalogName(catalog)}
                                imageUrl={catalog.image_logo_url}
                                className={styles.avatar}
                              />
                            ))}
                          </div>
                          <Flex direction="column" className={styles.rowText}>
                            <Flex align="center" gap="1">
                              {group.isFavourites && (
                                <RxStarFilled
                                  aria-hidden="true"
                                  className={styles.favouriteIcon}
                                />
                              )}
                              <Text weight="medium" truncate>
                                {group.name}
                              </Text>
                            </Flex>
                            <Text size="1" color="gray" translate="no" truncate>
                              {members.map(catalogName).join(', ')}
                            </Text>
                          </Flex>
                        </Flex>
                      </Text>
                    </Flex>
                  )
                })
              )}
            </Box>
          </Tabs.Content>
        </Tabs.Root>

        <Flex justify="between" align="center" mt="4" gap="3">
          <Text size="1" color="gray">
            {selectedCount} selected
          </Text>
          <Flex gap="3">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              onClick={() => {
                onApply(draft)
                onOpenChange(false)
              }}
            >
              Apply
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
